import React from 'react'
import { boolean, number, object, setLocale, string } from 'yup'
import { ColumnProps } from 'antd/lib/table'
import { Dropdown, Tag, Tooltip } from 'antd'
import { FormikProps } from 'formik'

import {
  Breadcrumb,
  Button,
  Menu,
  MenuItem,
  Text,
  ToolIcon
} from 'src/components/Ant'
import { Currency } from 'src/types'
import { stringify } from 'src/core/hooks/use-translation'

import SelectCurrencyGroup from './select-currency-group'

import { DataColumnsProps } from '../../../components/resources-crud'

export function getDataColumns({
  editCurrent
}: DataColumnsProps): ColumnProps<unknown>[] {
  return [
    {
      dataIndex: 'code',
      render: (code: string) => {
        if (code?.length > 50) {
          return <Tooltip title={code}>{`${code.substring(0, 47)}...`}</Tooltip>
        }
        return <Text>{code}</Text>
      },
      title: 'web.resources.common.code.title'
    },
    {
      dataIndex: 'name',
      elipsis: true,
      render: (name: string = '') => {
        if (name?.length > 50) {
          return <Tooltip title={name}>{`${name.substring(0, 47)}...`}</Tooltip>
        }
        return <Text>{name}</Text>
      },
      title: 'web.resources.common.name.title'
    },
    {
      dataIndex: 'decimal_digits',
      render: (decimal: string, _record: Currency) => <Text>{decimal}</Text>,
      title: 'web.resources.currencies.decimal-digits'
    },
    {
      dataIndex: 'standard',
      render: (isStandard: boolean) =>
        isStandard && (
          <Tag color="blue">
            <Text title="web.resources.common.standard" />
          </Tag>
        ),
      title: 'web.resources.common.standard'
    },
    {
      dataIndex: 'actions',
      render: (_: any, record: Currency) => {
        const actionEdit = 'web.administration.crud.actions.edit'
        const actionSetStandard = record?.standard
          ? 'web.administration.crud.actions.standard-remove'
          : 'web.administration.crud.actions.standard-set'
        const actionSetUnlink = 'common.actions.unlink'

        return (
          <Dropdown
            overlay={
              <Menu
                onClick={async ({ key }: any) => {
                  switch (key) {
                    case 'set-standard':
                      editCurrent(
                        {
                          ...record,
                          standard: !record.standard
                        },
                        true
                      )
                      break
                    case 'edit':
                      editCurrent(record, false)
                      break
                    case 'set-unlink':
                      editCurrent({ ...record, active: false }, true)
                      break
                  }
                }}
              >
                <MenuItem key="set-standard" title={actionSetStandard} />
                <MenuItem key="edit" title={actionEdit} />
                <MenuItem key="set-unlink" title={actionSetUnlink} />
              </Menu>
            }
            placement="bottomRight"
          >
            <Button type="link">
              <ToolIcon type="more" style={{ fontWeight: 'bold' }} />
            </Button>
          </Dropdown>
        )
      },
      width: 50
    }
  ] as ColumnProps<unknown>[]
}

export function getDrawer() {
  return function DrawerContent(
    formikProps: FormikProps<Currency>
  ): JSX.Element {
    if (!formikProps) {
      return <span />
    }

    return <SelectCurrencyGroup formikProps={formikProps} />
  }
}

export const getDrawerWidth = '40%'

export const getHeader = (
  <Breadcrumb
    items={[
      'web.administration.title',
      'web.resources.title-plural',
      'web.resources.currencies.title-plural'
    ]}
  />
)

export function getInitialValues() {
  return {
    active: true,
    code: '',
    currency_id: null,
    decimal_digits: 2,
    name: '',
    reference_currency_id: null,
    standard: false
  } as Currency
}

export const getTitle = 'web.resources.currencies.title'

export function getValidationSchema() {
  setLocale({
    mixed: {
      required: 'common.required',
      notType: 'common.required'
    }
  })

  return object().shape({
    active: boolean().required(),
    code: string()
      .max(
        250,
        stringify('web.setup.form.field-title.max-character-error', {
          value: 250
        })
      )
      .required(),
    decimal_digits: number()
      .moreThan(-1, 'common.required')
      .max(
        8,
        stringify('common.yup.field-max-value', {
          value: 8
        })
      )
      .required(),
    name: string()
      .trim()
      .max(
        250,
        stringify('web.setup.form.field-title.max-character-error', {
          value: 250
        })
      )
      .required(),
    standard: boolean().required()
  })
}
